import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../../lib/constants';
import { DRAFT_KEYS, loadDraft, saveDraft, clearDraft, hasDraft } from '../../../../utils/draftUtils';
import { useNotification } from '../../../../contexts/NotificationContext';

export const useIntraOpNursingRecord = (uhid_no, navigate) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [errors, setErrors] = useState({});
  const [patient, setPatient] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: uhid_no || "",
    diagnosis: "",
    name_of_surgery: "",
    or_no: "",
    wheel_in_time: "",
    wheel_out_time: "",
    anesthesia_start_time: "",
    anesthesia_end_time: "",
    incision_time: "",
    closure_time: "",
    mode_of_transfer_to_ot: "",
    anesthesia_type: "",
    position: "",
    pressure_protection_pad: {
      Head_rest: false,
      Sand_bag: false,
      "Pillow": false,
      "Axillary Roll": false,
      "Shoulder Roll": false,
      "Eye protection": false,
      "Chest Roll": false,
      "Sling": false,
      "Boot": false,
      "Stirrups": false,
      "Leg holder": false,
      "L Arm padded, tucked & secured": false,
      "R Arm padded, tucked & secured": false,
    },
    pressure_protection_pad_other: "",
    skin_preparation: "",
    skin_preparation_other: "",
    monopolar_pad_location: "",
    tourniquet_location: {
      tourniquet_location_1: "",
      tourniquet_location_2: "",
      tourniquet_location_3: "",
      tourniquet_location_4: "",
      tourniquet_location_5: "",
      tourniquet_location_6: "",
    },
    applied_time: {
      applied_time_1: "",
      applied_time_2: "",
      applied_time_3: "",
      applied_time_4: "",
      applied_time_5: "",
      applied_time_6: "",
    },
    released_time: {
      released_time_1: "",
      released_time_2: "",
      released_time_3: "",
      released_time_4: "",
      released_time_5: "",
      released_time_6: "",
    },
    other_equipment_used: {
        equipment_1: "",
        equipment_2: "",
        equipment_3: "",
        equipment_4: "",
        equipment_5: "",
        equipment_6: "",
    },
    prbc_units: {
      prbc_units_1: "",
      prbc_units_2: "",
      prbc_units_3: "",
    },
    ffp_units: {
      ffp_units_1: "",
      ffp_units_2: "",
      ffp_units_3: "",
    },
    platelets_units: {
      platelets_units_1: "",
      platelets_units_2: "",
      platelets_units_3: "",
    },
    other_blood_units: {
      other_blood_units_1: "",
      other_blood_units_2: "",
      other_blood_units_3: "",
    },
    name_of_surgeon: "",
    name_of_assistant: "",
    name_of_anesthetist: "",
    name_of_assistant_anesthesia: "",
    packing_type: {
      packing_type_1: "",
      packing_type_2: "",
      packing_type_3: "",
      packing_type_4: "",
      packing_type_5: "",
      packing_type_6: "",
    },
    packing_size: {
      packing_size_1: "",
      packing_size_2: "",
      packing_size_3: "",
      packing_size_4: "",
      packing_size_5: "",
      packing_size_6: "",
    },
    packing_site: {
      packing_site_1: "",
      packing_site_2: "",
      packing_site_3: "",
      packing_site_4: "",
      packing_site_5: "",
      packing_site_6: "",
    },
    packing_output: {
      packing_output_1: "",
      packing_output_2: "",
      packing_output_3: "",
      packing_output_4: "",
      packing_output_5: "",
      packing_output_6: "",
    },
    packing_sign: "",
    packing_sign2: "",
    packing_sign3: "",
    packing_sign4: "",
    packing_sign5: "",
    packing_sign6: "",
    nurses_notes: "",
    blood_transfusion: "",
    blood_transfusion_if_yes: "",
    condition_of_patient: "",
    type_of_specimen: "",
    lab: "",
    frozen_section_time_sent: "",
    frozen_section_time_received: "",
    name_of_pathologist: "",
    condition_of_patient_text: "",
    transferred_to_recovery_icu: "",
    scrub_nurse_signature: "",
    scrub_nurse_name: "",
    scrub_nurse_datetime: "",
    circulating_nurse_signature: "",
    circulating_nurse_name: "",
    circulating_nurse_datetime: "",
    name_of_implant: "",
  });

  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const fetchFormRecord = async () => {
      try {
          const response = await axios.get(`${API_BASE_URL}/get_intra_op_record/${uhid_no}`);
          const data = response.data;
          if (data) {
              const jsonFields = [
                  'applied_time', 'released_time', 'tourniquet_location', 'other_equipment_used',
                  'prbc_units', 'ffp_units', 'platelets_units', 'other_blood_units',
                  'packing_type', 'packing_size', 'packing_site', 'packing_output',
              ];

              jsonFields.forEach((key) => {
                  if (typeof data[key] === 'string' && data[key] !== "") {
                      try {
                          data[key] = JSON.parse(data[key]);
                      } catch (error) {
                          console.warn(`Could not parse ${key}:`, error);
                      }
                  }
              });

              if (typeof data.pressure_protection_pad === 'string' && data.pressure_protection_pad !== "") {
                  try {
                      const selectedPads = JSON.parse(data.pressure_protection_pad);
                      data.pressure_protection_pad = Object.keys(formData.pressure_protection_pad).reduce((acc, pad) => {
                          acc[pad] = selectedPads.includes(pad);
                          return acc;
                      }, {});
                  } catch (error) {
                      console.warn('Could not parse pressure_protection_pad:', error);
                  }
              }

              const stripSeconds = (time) => {
                  if (!time) return "";
                  const parts = time.split(":");
                  return parts.length >= 2 ? `${parts[0].padStart(2, "0")}:${parts[1]}` : time;
              };

              const dateTimeFields = ["circulating_nurse_datetime", "scrub_nurse_datetime"];
              const formattedDateTimes = {};
              dateTimeFields.forEach(field => {
                  if (data[field]) {
                      formattedDateTimes[field] = new Date(data[field]).toISOString().slice(0, 16);
                  }
              });

              setFormData(prev => ({
                ...prev,
                ...data,
                wheel_in_time: stripSeconds(data.wheel_in_time),
                wheel_out_time: stripSeconds(data.wheel_out_time),
                anesthesia_start_time: stripSeconds(data.anesthesia_start_time),
                anesthesia_end_time: stripSeconds(data.anesthesia_end_time),
                incision_time: stripSeconds(data.incision_time),
                closure_time: stripSeconds(data.closure_time),
                frozen_section_time_sent: stripSeconds(data.frozen_section_time_sent),
                frozen_section_time_received: stripSeconds(data.frozen_section_time_received),
                ...formattedDateTimes,
              }));
              setSuccess("success");
          }
      } catch (error) {
          console.error("Error loading intra-op record:", error);
      }
  };

  useEffect(() => {
    const init = async () => {
        setLoading(true);
        try {
            const ipRes = await axios.get(`${API_BASE_URL}/intra_op_next_ip`);
            setFormData(prev => ({ ...prev, ip_no: ipRes.data.next_ip_no }));
            await fetchFormRecord();
        } catch (error) {
            console.error('Initialization error:', error);
        } finally {
            setLoading(false);
            setDataLoaded(true);
        }
    };
    init();
  }, [uhid_no]);

  // Draft handling
  useEffect(() => {
    if (!dataLoaded) return;
    const draftData = loadDraft(DRAFT_KEYS.INTRAOPERATIVE_NURSING_RECORD, uhid_no);
    if (draftData) {
      setFormData(prev => ({ ...prev, ...draftData.formData }));
    }
  }, [uhid_no, dataLoaded]);

  useEffect(() => {
    if (!dataLoaded) return;
    const timeoutId = setTimeout(() => {
        saveDraft(DRAFT_KEYS.INTRAOPERATIVE_NURSING_RECORD, uhid_no, formData);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded, uhid_no]);

  const handleChange = (event) => {
    if (!event.target) return;
    const { name, type, checked, value } = event.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSignatureOpen = (signatureKey) => {
    const datetimeKey =
      signatureKey === "scrub_nurse_signature"
        ? "scrub_nurse_datetime"
        : signatureKey === "circulating_nurse_signature"
          ? "circulating_nurse_datetime"
          : null;

    if (datetimeKey) {
      const now = new Date().toISOString().slice(0, 16);
      setFormData((prev) => ({ ...prev, [datetimeKey]: now }));
    }

    setCurrentSignatureRole(signatureKey);
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();
    const signatureBase64 = dataURL.split(",")[1];
    setFormData((prev) => ({
      ...prev,
      [currentSignatureRole]: signatureBase64,
    }));
    setSignatureDialogOpen(false);
  };

  const validate = () => {
    const tempErrors = {};
    const required = ['diagnosis', 'name_of_surgery', 'or_no', 'monopolar_pad_location', 'name_of_surgeon', 'name_of_anesthetist'];
    required.forEach(field => {
        if (!formData[field]) tempErrors[field] = "Required";
    });
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!validate()) {
        showNotification('error', "Please fill in all required fields.");
        return;
    }

    const prepareSelected = (obj) => {
        return Object.keys(obj).filter(k => obj[k]).reduce((acc, key) => { acc[key] = obj[key]; return acc; }, {});
    };

    const data = {
      ...formData,
      hospital_id: 1,
      pressure_protection_pad: Object.keys(formData.pressure_protection_pad).filter(k => formData.pressure_protection_pad[k]),
      tourniquet_location: prepareSelected(formData.tourniquet_location),
      applied_time: prepareSelected(formData.applied_time),
      released_time: prepareSelected(formData.released_time),
      other_equipment_used: prepareSelected(formData.other_equipment_used),
      packing_type: prepareSelected(formData.packing_type),
      packing_size: prepareSelected(formData.packing_size),
      packing_site: prepareSelected(formData.packing_site),
      packing_output: prepareSelected(formData.packing_output),
      prbc_units: prepareSelected(formData.prbc_units),
      ffp_units: prepareSelected(formData.ffp_units),
      platelets_units: prepareSelected(formData.platelets_units),
      other_blood_units: prepareSelected(formData.other_blood_units),
      blood_transfusion: formData.blood_transfusion || "No",
    };

    try {
      setLoading(true);
      if (success) {
        await axios.put(`${API_BASE_URL}/update_intra_op_record/${uhid_no}`, data, { withCredentials: true });
        showNotification('success', "Record updated successfully!");
      } else {
        await axios.post(`${API_BASE_URL}/intra_op_record`, data, { withCredentials: true });
        showNotification('success', 'Record submitted successfully!');
      }
      clearDraft(DRAFT_KEYS.INTRAOPERATIVE_NURSING_RECORD, uhid_no);
      navigate(`/mainPage/${uhid_no}`, { state: { from: 'IntraOperativePhase', status: 'success', step: 2 } });
    } catch (error) {
      console.error('Submission error:', error);
      showNotification('error', "Error in submitting form!");
    } finally {
      setLoading(false);
    }
  };

  return {
    formData, setFormData, errors, loading, success,
    signatureDialogOpen, setSignatureDialogOpen, signaturePadRef,
    handleChange, handleSignatureOpen, handleSignatureSave, handleSubmit
  };
};
